import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert } from 'react-bootstrap';
import { FaLink, FaCopy, FaEnvelope, FaCheck } from 'react-icons/fa';
import axios from 'axios';
import Swal from 'sweetalert2';

const OnboardingLinkGenerator = () => {
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    company: ''
  });
  
  const [generatedLink, setGeneratedLink] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const generateLink = async () => {
    if (!formData.email) {
      Swal.fire({
        icon: 'warning',
        title: 'Email Required',
        text: 'Please enter an email address to generate the onboarding link.',
        confirmButtonText: 'OK',
        confirmButtonColor: '#2e3192'
      });
      return;
    }

    setIsGenerating(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/generate-onboarding-link`, {
        ...formData,
        source: 'email' // Default to email invitation
      });
      
      if (response.data.success) {
        setGeneratedLink(response.data.shareableLink);
        Swal.fire({
          icon: 'success',
          title: 'Link Generated!',
          text: 'Your shareable onboarding link has been created successfully.',
          confirmButtonText: 'Great!',
          confirmButtonColor: '#2e3192'
        });
      } else {
        throw new Error('Failed to generate link');
      }
    } catch (error) {
      console.error('Error generating link:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to generate the onboarding link. Please try again.',
        confirmButtonText: 'OK',
        confirmButtonColor: '#2e3192'
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generatedLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      
      Swal.fire({
        icon: 'success',
        title: 'Copied!',
        text: 'The onboarding link has been copied to your clipboard.',
        timer: 2000,
        showConfirmButton: false,
        toast: true,
        position: 'top-end'
      });
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      Swal.fire({
        icon: 'error',
        title: 'Copy Failed',
        text: 'Could not copy to clipboard. Please copy manually.',
        confirmButtonText: 'OK',
        confirmButtonColor: '#2e3192'
      });
    }
  };

  const sendEmail = async () => {
    if (!formData.email || !generatedLink) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing Information',
        text: 'Please generate a link first and ensure email is provided.',
        confirmButtonText: 'OK',
        confirmButtonColor: '#2e3192'
      });
      return;
    }

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/send-onboarding-email`, {
        email: formData.email,
        name: formData.name,
        company: formData.company,
        shareableLink: generatedLink,
        source: 'email' // Default to email invitation
      });

      if (response.data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Email Sent!',
          text: `Email has been sent to ${formData.email} from vibes@knackhook.com`,
          confirmButtonText: 'Great!',
          confirmButtonColor: '#2e3192'
        });
      } else {
        throw new Error('Failed to send email');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      Swal.fire({
        icon: 'error',
        title: 'Email Failed',
        text: 'Failed to send the invitation email. Please try again.',
        confirmButtonText: 'OK',
        confirmButtonColor: '#2e3192'
      });
    }
  };

  return (
    <Container className="mt-5">
      <Row className="justify-content-center">
        <Col md={8} lg={6}>
          <Card className="shadow">
            <Card.Header style={{ backgroundColor: '#2e3192', color: 'white' }}>
              <h5 className="mb-0">
                <FaLink className="me-2" />
                Generate Shareable Onboarding Link
              </h5>
            </Card.Header>
            <Card.Body className="p-4">
              <Alert variant="info">
                <strong>How it works:</strong> Generate a personalized onboarding link that you can share via email. When someone clicks the link, they'll be taken directly to the onboarding process with their information pre-filled.
              </Alert>

              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Email Address *</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter recipient's email"
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Name (Optional)</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter recipient's name"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Company (Optional)</Form.Label>
                  <Form.Control
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    placeholder="Enter company name"
                  />
                </Form.Group>


                <div className="d-grid">
                  <Button
                    variant="primary"
                    onClick={generateLink}
                    disabled={isGenerating || !formData.email}
                    style={{ backgroundColor: '#2e3192', borderColor: '#2e3192' }}
                  >
                    {isGenerating ? 'Generating...' : 'Generate Link'}
                  </Button>
                </div>
              </Form>

              {generatedLink && (
                <div className="mt-4">
                  <Alert variant="success">
                    <h6><FaCheck className="me-2" />Link Generated Successfully!</h6>
                    <p className="mb-2">Your shareable onboarding link:</p>
                    <div className="input-group">
                      <Form.Control
                        type="text"
                        value={generatedLink}
                        readOnly
                        className="font-monospace"
                      />
                      <Button
                        variant="outline-secondary"
                        onClick={copyToClipboard}
                        title="Copy to clipboard"
                      >
                        {copied ? <FaCheck /> : <FaCopy />}
                      </Button>
                    </div>
                  </Alert>

                  <div className="d-grid gap-2">
                    <Button
                      variant="outline-primary"
                      onClick={sendEmail}
                      style={{ borderColor: '#2e3192', color: '#2e3192' }}
                    >
                      <FaEnvelope className="me-2" />
                      Send via Email
                    </Button>
                  </div>

                  <Alert variant="light" className="mt-3">
                    <small>
                      <strong>What happens next:</strong><br />
                      • The recipient will receive a personalized onboarding link<br />
                      • When they click the link, their information will be pre-filled<br />
                      • They'll go through the complete onboarding process<br />
                      • You can track their progress and completion
                    </small>
                  </Alert>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default OnboardingLinkGenerator;
